import * as angular from 'angular';
import { config } from 'angular/utils/config';

(function () {
    'use strict';

    angular.module('app').directive('pkpWizard', pkpWizard);

    /**
     * Directive for wizards
     *
     * In order for proper functionality your have to provide a stage-Routeparam in app.route.js
     *
     * @author bjnowak.btc
     * @ngInject
     */
    function pkpWizard(
        $log,
        $window,
        $routeParams,
        $route,
        storageService,
        DATES
    ) {
        return {
            restrict: 'E',
            scope: {
                /**
                 * Will be displayed as wizard name
                 */
                title: '@',

                /**
                 * Has to be an object like
                 * {
                 *     1: {title: 'Great title', page: '<full path to html>'},
                 *     2: {title: 'Great title', page: '<full path to html>'},
                 *     3: {title: 'Great title', page: '<full path to html>'}
                 * }
                 */
                stages: '=',

                /**
                 * {number} If present the wizard will start at given stage
                 */
                startWith: '=?',

                /**
                 * Full path to a html
                 */
                inputSummary: '=',

                /**
                 * Data to be used for the input summary
                 */
                summary: '=inputSummaryData',

                /**
                 * Should be the same string as used by correspnding controllers/services to persist data with storageService
                 */
                STORAGE_PREFIX: '=storagePrefix',

                /**
                 * Can be used to give input summary or stages some access to corresponding main controller
                 */
                service: '=?',

                /**
                 * If present and true everything will be stored in the session storage, else to local
                 */
                storeInSession: '@?',

                /**
                 * Will be called if {@link goToNextStage} were called
                 */
                onNext: '&?',

                /**
                 * Will be called if back button were pressed on first stage
                 */
                onRestart: '&?',

                /**
                 * Will be called if internal {@link finished) were called. Can return a promise.
                 */
                onFinished: '&?',

                /**
                 * Will be called if internal {@link goToErrorPage) were called.
                 */
                onError: '&?',

                /**
                 * Will be called if a uses uses the step navigation to go to a specific step
                 */
                onGoingToStep: '&?',

                /**
                 * If not present or true the wizard will perform a reset on finished, else do nothing
                 */
                resetOnFinished: '=?',

                /**
                 * If the user goes to a previous page, higher pages can't be accessed directly (the numbers will be disabled again)
                 */
                noGoingForwardAfterGoingBack: '=?'
            },
            template: require('app/_directives/pkp-wizard.directive.html'),
            link: postLink
        };

        function postLink(scope) {
            type GoToStageParams = {
                index: number;
                fromBreadcrumb: boolean;
                fromSummaryCard: boolean;
            };

            scope.DATES = DATES;

            scope.stageConfig = config;

            /**
             * In order to have a clearer view on wizard controls.
             */
            scope.wizard = {
                currentStageIndex: 1,
                goToTop: goToTop,
                goToStage: goToStage,
                goToNextStage: goToNextStage,
                finished: finished,
                previousStage: previousStage,
                isOnStage: isOnStage,
                isStageEnabled: isStageEnabled,
                prettifyGasType: prettifyGasType,
                setFarthestStageIndexToCurrentStage:
                    setFarthestStageIndexToCurrentStage,
                goToErrorPage: goToErrorPage
            };

            // determine which storage to be used
            var storage = 'Local';
            if (!!scope.storeInSession) {
                storage = 'Session';
            }

            if (angular.isUndefined(scope.resetOnFinished)) {
                scope.resetOnFinished = true;
            }

            if (!!scope.startWith) {
                scope.wizard.currentStageIndex = parseInt(scope.startWith);
                updateFarthestStageIndex();
            } else {
                if (angular.isUndefined($routeParams.stage)) {
                    if (!angular.isUndefined(scope.onRestart)) {
                        scope.onRestart();
                    }
                    reset();
                } else {
                    if (
                        Object.keys(scope.stages).indexOf($routeParams.stage) >
                            -1 &&
                        isStageEnabled($routeParams.stage)
                    ) {
                        scope.wizard.currentStageIndex = parseInt(
                            $routeParams.stage
                        );
                    } else {
                        $log.debug(
                            'Stage ' +
                                $routeParams.stage +
                                ' is not a valid stage number or not enabled, starting with stage 1.'
                        );
                        $route.updateParams({ stage: 1 });
                    }
                    updateFarthestStageIndex();
                }
            }

            function goToTop() {
                $window.scrollTo(0, 0);
            }

            /**
             * Should be called by stage controllers after form data has been saved
             *
             * Reload page with new stage and increase farthest stage index
             */
            function goToNextStage() {
                if (scope.onNext) {
                    scope.onNext();
                }
                $route.updateParams({
                    stage: ++scope.wizard.currentStageIndex
                });
                updateFarthestStageIndex();
            }

            function goToErrorPage() {
                scope.onError();
                reset();
                scope.onRestart();
            }

            /**
             * Reset Wizard only if resetOnFinished equals true and onFinished is not defined
             * or on success callback when onFinished returned a promise
             */
            function finished() {
                if (scope.onFinished) {
                    var promise = scope.onFinished();
                    if (!angular.isUndefined(promise)) {
                        promise.then(function () {
                            if (scope.resetOnFinished) {
                                reset();
                            }
                        }, null);
                        return promise;
                    }
                }
                if (scope.resetOnFinished) {
                    reset();
                }
            }

            /**
             * If <i>index</i> is an enabled stage the page will be reloaded with new stage
             * There wont be a check for unsubmitted values
             *
             * @param {number} index must be an integer
             * @param {doNotTriggerNavEvent} whether to call the navigation callback
             */
            function goToStage({
                index,
                fromBreadcrumb,
                fromSummaryCard
            }: GoToStageParams) {
                const previousIndex = scope.wizard.currentStageIndex;
                if (isStageEnabled(index)) {
                    $route.updateParams({ stage: index });
                    if (!!scope.noGoingForwardAfterGoingBack) {
                        setFarthestStageIndexTo(index);
                    }
                    if (scope.onGoingToStep) {
                        scope.onGoingToStep({
                            index,
                            fromBreadcrumb,
                            fromSummaryCard,
                            previousIndex
                        });
                    }
                }
            }

            /**
             * Used by: breadcrumb navigation
             *
             * Reload page with previous stage
             * If called on first stage the wizard should be reset
             */
            function previousStage() {
                $route.updateParams({
                    stage: --scope.wizard.currentStageIndex
                });
                if (!!scope.noGoingForwardAfterGoingBack) {
                    setFarthestStageIndexToCurrentStage();
                }
            }

            function isOnStage(index) {
                return scope.wizard.currentStageIndex == index;
            }

            /**
             * Determine when a stage is enabled
             * Currently if only given stage index is smaller or equals farthest stage index
             *
             * @param {number} index must be an integer
             * @returns {boolean}
             */
            function isStageEnabled(index) {
                return index <= getFarthestStageIndex();
            }

            function reset() {
                scope.wizard.currentStageIndex = 1;
                store('farthestStageIndex', 1);
            }

            /**
             * Farthest stage index displays how far the user has been
             * Will be changed by {@link updateFarthestStageIndex}
             *
             * @returns {number} will be an integer
             *
             * @see updateFarthestStageIndex
             */
            function getFarthestStageIndex() {
                var int = parseInt(fetch('farthestStageIndex'));
                return isNaN(int) ? undefined : int;
            }

            /**
             * Farthest stage index will be increased by {@link goToNextStage} or may decreased by {@link reset}
             *
             * @see goToNextStage
             * @see reset
             */
            function updateFarthestStageIndex() {
                if (scope.wizard.currentStageIndex <= -1) {
                    store('farthestStageIndex', 1);
                    return;
                }
                if (
                    angular.isUndefined(getFarthestStageIndex()) ||
                    scope.wizard.currentStageIndex > getFarthestStageIndex()
                ) {
                    store('farthestStageIndex', scope.wizard.currentStageIndex);
                }
            }

            function setFarthestStageIndexToCurrentStage() {
                store('farthestStageIndex', scope.wizard.currentStageIndex);
            }

            function setFarthestStageIndexTo(index: number) {
                store('farthestStageIndex', index);
            }

            function parseKey(key) {
                return scope.STORAGE_PREFIX + key;
            }

            function store(key, value) {
                storageService['storeTo' + storage](parseKey(key), value);
            }

            function fetch(key) {
                return storageService['fetchFrom' + storage](parseKey(key));
            }

            /**
             * TO replace underscore with hyphen
             * @param gasType
             * @returns {*}
             */
            function prettifyGasType(gasType) {
                if (!angular.isUndefined(gasType)) {
                    return gasType.replace(/_/g, '-');
                }
            }
        }
    }
})();
